/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import { faUpload } from '@fortawesome/free-solid-svg-icons/faUpload'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import useQueryParams from '../../../../../client/js/hooks/useQueryParams'
import usePaginationQuery from '../../../../../client/js/components/pagination/usePaginationQuery'
import {
  deleteLeaseTemplate,
  findManyLeaseTemplates,
  postLeaseTemplateFile
} from '../../../lib/rest/leaseTemplateAPIClient'
import useSelection from '../../../../../client/js/components/selection/useSelection'
import { selectFile } from '../../../../../client/js/lib/download'
import LoadingHelper from '../../../../../client/js/components/LoadingHelper'
import PageTitle from '../../../../../client/js/components/PageTitle'
import ListCard from '../../../../../client/js/components/ListCard'
import RequirePerm from '../../../../auth/components/RequirePerm'
import SearchForm from '../../../../../client/js/components/search/SearchForm'
import Filters from '../../../../../client/js/components/Filters'
import SelectionAlert from '../../../../../client/js/components/selection/SelectionAlert'
import LeaseTemplateList from './LeaseTemplateList'
import UploadingModal from '../../UploadingModal'
import DeleteSelectionModal from '../../../../../client/js/components/modals/DeleteSelectionModal'
import { Button, Container } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import Toolbar from '../../../../../client/js/components/Toolbar'
import { useSettingsContext } from '../../SettingsProvider'
import Page from '../../../../../client/js/components/Page'
import AnchorLink from '../../../../../client/js/components/AnchorLink'
import BreadcrumbCard from '../../../../../client/js/components/breadcrumb/BreadcrumbCard'
import AdminBreadcrumb from '../../../../../client/js/components/breadcrumb/AdminBreadcrumb'
import { getLeaseListLink } from '../../../lib/links/leaseLinks'

export type LeaseTemplateListQuery = {
  limit?: string,
  page?: string,
  search?: string,
}

const initialQuery: LeaseTemplateListQuery = {
  limit: '10',
  page: '1'
}

function LeaseTemplateListPage () {
  const intl = useIntl()

  const title = intl.formatMessage({
    id: 'lease_templates',
    defaultMessage: 'Modèles de bail'
  })

  const [searchParams, setSearchParams] = useQueryParams()
  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const { settings } = useSettingsContext()

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...searchParams,
      page,
      limit
    }
  }, [limit, page, searchParams])

  // Items loading.
  const listQuery = useQuery({
    enabled: true,
    queryKey: ['leaseTemplates', queryParams],
    queryFn: () => findManyLeaseTemplates(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })
  const { refetch } = listQuery
  const items = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.leaseTemplates : null) || []
  ), [listQuery.data])

  const setFilter = useCallback((name: string, value: string) => {
    setSearchParams((s) => ({ ...s, page: '1', [name]: value }))
  }, [setSearchParams])

  const setSearch = useCallback((search: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const handleDelete = useCallback((id: number) => deleteLeaseTemplate(id), [])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Items filtering.
  const [isFiltersVisible] = useState<boolean>(true)

  // Template uploading.
  const uploader = useMutation({
    mutationFn: async (file: File) => postLeaseTemplateFile(file),
    onSuccess: refetch,
    onError (err) {
      notifications.show({
        message: err.message,
        color: 'red',
        autoClose: false
      })
    }
  })
  const { mutateAsync: upload } = uploader
  const handleUpload = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    selectFile(upload, { accept: settings.LEASE_TEMPLATE_FILE_TYPES?.join(',') })
  }, [settings.LEASE_TEMPLATE_FILE_TYPES, upload])

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={title}
        />

        <BreadcrumbCard>
          <AdminBreadcrumb>
            <AnchorLink to={getLeaseListLink()}>
              <FormattedMessage
                id="rental_files"
                defaultMessage="Dossiers de location"
              />
            </AnchorLink>
            <span>{title}</span>
          </AdminBreadcrumb>
        </BreadcrumbCard>

        <ListCard
          filters={<Filters in={isFiltersVisible} />}
          pagination={pagination}
          selection={selection}
          selectionActions={<SelectionAlert>
            <RequirePerm
              permissions={['write:lease-template']}
              render={() => (
                <Button onClick={toggleDeleteModal}>
                  <FontAwesomeIcon
                    fixedWidth
                    icon={faTrash}
                  />
                </Button>
              )}
            />
          </SelectionAlert>}
          toolbar={<Toolbar>
            <RequirePerm
              permissions={['write:lease-template']}
              render={() => (
                <Button
                  variant="filled"
                  disabled={uploader.isPending}
                  onClick={handleUpload}
                  leftSection={<FontAwesomeIcon fixedWidth icon={faUpload} />}
                >
                  <FormattedMessage
                    id="upload_lease_template"
                    defaultMessage="Charger un modèle ({acceptedTypes})"
                    values={{ acceptedTypes: intl.formatList(['.docx']) }}
                  />
                </Button>
              )}
            />
            <SearchForm
              initialValue={queryParams.search}
              onSubmit={setSearch}
              submitting={listQuery.isFetching}
            />
            <Button
              loading={listQuery.isFetching}
              onClick={() => {
                listQuery.refetch()
              }}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faSyncAlt}
              />
            </Button>
          </Toolbar>}
        >
          <LoadingHelper
            query={listQuery}
            render={() => (
              <LeaseTemplateList items={items} />
            )}
          />
        </ListCard>
      </Container>

      <UploadingModal uploader={uploader} />

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            listQuery.refetch()
          }}
          onClose={toggleDeleteModal}
        />
        : null}
    </Page>
  )
}

export default LeaseTemplateListPage
